// The Home screen's POP tag scan: the round button and the panel that answers it (Session APG).
// "POP" is Proof Of Presence: the NFC sticker on the paper log card in a lift's motor room.
//
// PLACEMENT, from the Director's own words: a green (#63C41F) circular button with a target style icon
// (not the ELE logo), in the top row of Home to the RIGHT of the greeting, right-aligned. ALWAYS
// there: it does not wait for a job to be open, because a tag names its own lift. EngineerHome owns
// the row; this file owns what sits in it and what appears under it.
//
// TWO PIECES, ONE HOOK. The button and the panel live in different places on the screen but answer to
// the same scan, so EngineerHome calls useHomeTagScan once and hands the result to both. What a scan
// does, and why, is at the head of usePopTagHomeScan.ts.
//
// IT IS A WAY OF KEEPING TIME, AND NOTHING ELSE. It logs an arrival or a leaving against a job. It says
// nothing about the engineer's welfare and no wording here may suggest it does, and it is deliberately
// NOT styled like any other control on Home: the round green scan target is its own thing. The
// separation test beside these files reads this source, and the hook's, and fails if either ever
// reaches for that vocabulary.
import { Crosshair } from "lucide-react";
import { bookedLine, candidateLabel, endPromise } from "../../lib/popTagHome";
import type { HomeTagScan } from "./usePopTagHomeScan";

// The text a phone that cannot read a tag is shown when the button is pressed. The button is always
// on Home (the Director asked for that), so a phone with no Web NFC is told, in place, what to do
// instead. The standalone Scan POP Tag screen says the same thing in the same words.
const UNSUPPORTED_BODY =
  "In-app scanning needs Chrome on Android. Tap the sticker with your phone in the usual way instead: it opens the same page and records the visit. Please sign the log card either way.";

export function PopTagScanButton({ scan }: { scan: HomeTagScan }) {
  return (
    <button
      type="button"
      onClick={scan.press}
      disabled={scan.busy}
      aria-busy={scan.busy}
      aria-label="Scan the lift's POP tag"
      title="Scan the POP tag"
      // 56px, so the target is comfortably over the 44px minimum even with a glove or a shaking hand
      // in a motor room. The fill is the brand green token (#63C41F) and the icon is the dark ink, not
      // white: white on this green measures about 2.2:1, and the platform's CTA rule uses dark ink on a
      // bright fill for exactly that reason.
      className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-ele-green text-ele-bg shadow-md disabled:opacity-60"
    >
      <Crosshair size={30} strokeWidth={2.25} aria-hidden="true" />
    </button>
  );
}

export function PopTagScanPanel({ scan }: { scan: HomeTagScan }) {
  const { summary, choosing, confirming } = scan;

  const nothingToShow =
    !scan.status &&
    !scan.message &&
    !scan.unconfirmed &&
    !scan.unsupportedShown &&
    !scan.cancelled &&
    !summary &&
    !choosing &&
    !confirming;
  if (nothingToShow) return null;

  return (
    <div className="mb-4 space-y-3">
      {scan.unsupportedShown ? (
        <div className="rounded-lg border border-ele-card-border bg-ele-card p-4" role="status">
          <h2 className="font-heading text-base text-ele-strong">Scanning is not available on this phone</h2>
          <p className="mt-1 text-sm text-ele-muted">{UNSUPPORTED_BODY}</p>
          <button type="button" onClick={scan.dismiss} className={QUIET_BUTTON}>
            Dismiss
          </button>
        </div>
      ) : null}

      {scan.status ? (
        <div className="rounded-lg border border-ele-card-border bg-ele-card p-4" role="status">
          <p className="text-sm text-ele-strong">{scan.status}</p>
          {/* Offered while checking, waiting for the tag and asking the server, as the job sheet's own
              step offers it while checking and scanning, and for the same reason: a motor room is the
              worst signal in the building, so a slow step is ordinary and there must be something to
              press. Not offered while recording: the tag has been read and the row is being written. */}
          {scan.cancellable ? (
            <button type="button" onClick={scan.cancel} className={QUIET_BUTTON}>
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}

      {/* The sign-in could not be confirmed. Amber, because it is neither a success nor a refusal:
          the scan goes ahead, and the answer says whether the engineer's name reached the record. */}
      {scan.unconfirmed ? (
        <p className="rounded-lg border border-ele-card-border bg-ele-card p-3 text-sm" role="status" style={{ color: "var(--ele-amber-text)" }}>
          Could not confirm you are signed in. The scan will go ahead, and you will be told whether your name reached the
          record.
        </p>
      ) : null}

      {scan.message ? (
        <p className="rounded-lg border border-ele-card-border bg-ele-card p-3 text-sm text-ele-body" role="alert">
          {scan.message}
        </p>
      ) : null}

      {/* MORE THAN ONE OPEN JOB AT THIS LIFT TODAY. The engineer says which job this tap is for, and
          only that: each button names the end it will log, so nothing is a surprise, but the end is
          never theirs to choose (each job's own recorded taps decide it). */}
      {choosing ? (
        <section aria-labelledby="pop-tag-pick" className="rounded-lg border border-ele-card-border bg-ele-card p-4">
          <h2 id="pop-tag-pick" className="font-heading text-base text-ele-strong">
            Which job is this tap for?
          </h2>
          <p className="mt-0.5 text-[13px] text-ele-muted">
            You have more than one open job at {choosing.siteName || choosing.assetRef}. Oldest first.
          </p>
          <ul className="mt-3 space-y-2">
            {choosing.candidates.map((c) => (
              <li key={c.jobId}>
                <button
                  type="button"
                  onClick={() => scan.choose(c)}
                  className="flex min-h-11 w-full items-center justify-between gap-3 rounded-ele border border-ele-card-border px-4 py-2 text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-heading text-[15px] font-bold text-ele-strong">{candidateLabel(c)}</span>
                    {bookedLine(c) ? <span className="block text-[13px] text-ele-muted">{bookedLine(c)}</span> : null}
                    <span className="block text-[13px] text-ele-muted">{endPromise(c.nextEnd)}</span>
                  </span>
                  <span aria-hidden="true" className="flex-none font-heading text-2xl" style={{ color: "var(--ele-green-ink)" }}>
                    &rsaquo;
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={scan.cancel} className={QUIET_BUTTON}>
            Cancel
          </button>
        </section>
      ) : null}

      {/* "LEAVING ALREADY?" A scan that would log a leaving inside three minutes of the job's own arrival:
          the platform's repeat window. Almost always a press that seemed to do nothing (or whose answer
          was lost with the signal after the server had written it) or a second press with the phone still
          on the tag, either of which would otherwise log a leaving seconds after the arrival. One tap
          says they really are leaving; the other keeps the arrival and records nothing. */}
      {confirming ? (
        <section aria-labelledby="pop-tag-confirm" className="rounded-lg border border-ele-card-border bg-ele-card p-4">
          <h2 id="pop-tag-confirm" className="font-heading text-base text-ele-strong">
            {confirming.heading}
          </h2>
          <p className="mt-0.5 text-[13px] font-bold text-ele-strong">{confirming.job}</p>
          <p className="mt-1 text-sm text-ele-muted">{confirming.body}</p>
          <button
            type="button"
            onClick={scan.confirm}
            className="mt-3 flex min-h-11 w-full items-center justify-center rounded-ele bg-ele-green px-4 font-heading text-sm tracking-wide text-ele-bg"
          >
            {confirming.yes}
          </button>
          <button type="button" onClick={scan.cancel} className={QUIET_BUTTON}>
            {confirming.no}
          </button>
        </section>
      ) : null}

      {scan.cancelled ? (
        <div className="rounded-lg border border-ele-card-border bg-ele-card p-4" role="status">
          <p className="text-sm text-ele-strong">Nothing was recorded. Press the scan button when you are ready.</p>
          <button type="button" onClick={scan.dismiss} className={QUIET_BUTTON}>
            Dismiss
          </button>
        </div>
      ) : null}

      {summary ? (
        // Theme-aware, never raw hex: the engineer shell has a LIGHT theme in which the dark-ground
        // greens and ambers this card would otherwise carry fail contrast. text-ele-green resolves to
        // the platform's own measured ink, and --ele-amber-text flips with the theme.
        <div
          role="status"
          className={`rounded-lg border bg-ele-card p-4 ${summary.tone === "ok" ? "border-ele-green" : "border-ele-card-border"}`}
        >
          <h2
            className={`font-heading text-base ${summary.tone === "ok" ? "text-ele-green" : summary.tone === "note" ? "text-ele-strong" : ""}`}
            style={summary.tone === "warn" ? { color: "var(--ele-amber-text)" } : undefined}
          >
            {summary.heading}
          </h2>
          {summary.lines.map((line, i) => (
            <p key={i} className="mt-1 text-sm text-ele-muted">
              {line}
            </p>
          ))}
          <button type="button" onClick={scan.dismiss} className={QUIET_BUTTON}>
            Dismiss
          </button>
        </div>
      ) : null}
    </div>
  );
}

const QUIET_BUTTON =
  "mt-2 flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-muted hover:text-ele-strong";
